import { useMemo } from 'react';
import { Layer, Marker, Source } from 'react-map-gl/mapbox';

import mapVenueClusterUrl from '@/assets/icons/map-venue-cluster.svg';
import {
  CLUSTER_MAX_ICON_SIZE,
  VENUE_CLUSTER_DISABLED_IMAGE_ID,
  VENUE_CLUSTER_ENABLED_IMAGE_ID,
  VENUE_DISABLED_COLOR,
  VENUE_ENABLED_COLOR,
  VENUES_CLUSTER_PROPERTIES,
  VENUES_SOURCE_ID,
  venueGlyphLayer,
  venuesClusterCountLayer,
  venuesClusterLayer
} from '../../layers.const';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { buildVenueAttributionLookup } from '../../MultiFlightMap.helpers';
import { useMapImage } from '../../MultiFlightMap.hooks';

import type { FeatureCollection } from 'geojson';

type Props = {
  mapId: string;
  position: { latitude: number; longitude: number } | null;
  temporaryPosition: { latitude: number; longitude: number } | null;
};

function VenuesMakers({ mapId, position, temporaryPosition }: Props) {
  const { attributionZones, mode, temporaryVenues } = useMultiFlightMap();

  useMapImage({
    color: VENUE_ENABLED_COLOR,
    height: 64,
    imageId: VENUE_CLUSTER_ENABLED_IMAGE_ID,
    mapId,
    maxIconSize: CLUSTER_MAX_ICON_SIZE,
    url: mapVenueClusterUrl,
    width: 48
  });
  useMapImage({
    color: VENUE_DISABLED_COLOR,
    height: 64,
    imageId: VENUE_CLUSTER_DISABLED_IMAGE_ID,
    mapId,
    maxIconSize: CLUSTER_MAX_ICON_SIZE,
    url: mapVenueClusterUrl,
    width: 48
  });

  const attributionState = useMemo(() => buildVenueAttributionLookup(attributionZones), [attributionZones]);

  const venuesFeatures = useMemo<FeatureCollection>(() => {
    return {
      features: temporaryVenues
        .filter(
          venue =>
            !venue.deleted &&
            venue._id != null &&
            venue.latlng &&
            typeof venue.latlng.lat === 'number' &&
            typeof venue.latlng.lng === 'number'
        )
        .map(venue => ({
          geometry: { coordinates: [venue.latlng!.lng, venue.latlng!.lat], type: 'Point' },
          properties: {
            attributionState: attributionState[String(venue._id)] ?? 'disabled',
            id: String(venue._id),
            kind: 'venue'
          },
          type: 'Feature'
        })),
      type: 'FeatureCollection'
    };
  }, [temporaryVenues, attributionState]);

  return (
    <>
      <Source
        cluster
        clusterMaxZoom={14}
        clusterProperties={VENUES_CLUSTER_PROPERTIES}
        clusterRadius={60}
        data={venuesFeatures}
        id={VENUES_SOURCE_ID}
        type="geojson"
      >
        <Layer {...venuesClusterLayer} />
        <Layer {...venuesClusterCountLayer} />
        <Layer {...venueGlyphLayer} />
      </Source>
      {temporaryPosition && (
        <Marker draggable={false} latitude={temporaryPosition.latitude} longitude={temporaryPosition.longitude}>
          <i className="fa-duotone fa-regular fa-location-dot multiFlightViewMap__cursorMarker" />
        </Marker>
      )}
      {mode === 'add-venues' && position && !temporaryPosition && (
        <Marker draggable={false} latitude={position.latitude} longitude={position.longitude}>
          <i className="fa-duotone fa-regular fa-location-dot multiFlightViewMap__cursorMarker" />
        </Marker>
      )}
      {mode === 'add-venues' && position && temporaryPosition && (
        <Marker draggable={false} latitude={position.latitude} longitude={position.longitude}>
          <i className="multiFlightViewMap__cursorMarker--temporary" />
        </Marker>
      )}
    </>
  );
}

export default VenuesMakers;
